import { Injectable } from '@angular/core';
import {GptAssistant} from "./assistant/GptAssistant";
import {GeminiAssistant} from "./assistant/GeminiAssistant";
import {HttpClient} from "@angular/common/http";
import {StoreSelectorsService} from "../store-selectors.service";
import {SceneAddService} from "../scene-add.service";
import {
    IAppearance, IBackground,
    IBlur, IBorder, ICRUDCommand,
    ICRUDRecord, ICRUDRecordReply, IGlow, IGradient, IPage,
    IPlayerProps, IResource,
    ISceneBlock,
    ISceneTreeSliceResults,
    ISelectedSceneSceneId,
    IShadow, IStationData, IUser
} from "../../interfaces";
import { IAiFunctionCalls } from "./interfaces/IAiFunctionCalls";
import {
    animationFrameScheduler,
    BehaviorSubject,
    concatMap,
    from,
    last,
    Observable,
    observeOn,
    of,
    Subject,
    switchMap,
    take
} from "rxjs";
import {
    actionAddResources, actionAddScene, actionCRUDRecord, actionCRUDRecordFailure,
    actionCRUDScene,
    actionUpdateSceneItem,
    actionUpdateScenePlayer
} from "../../store/actions/user.actions";
import {Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {JsonDiffDialogComponent} from "../../containers/ai/json-diff-dialog/json-diff-dialog.component";
import {TutorialsService} from "../tutorials.service";
import {Assistant} from "./assistant/assistant";
import {catchError, delay, finalize, map, tap} from "rxjs/operators";
import {ContainerService} from "../container.service";
import {componentsDefinition} from "./definitions/components-info";
import {AppService} from "../app.service";
import {LocalStoreService} from "../local-store.service";
import {UserService} from "../user.service";
import {generalInfo} from "./definitions/general-info";
import * as _ from "lodash";
import {SceneMimeTypes} from "../../containers/editor/scene-blocks/mime-constants";
import {preprocessSceneRecord} from "../../common";
import {TranslocoService} from "@ngneat/transloco";
import {SceneListService} from "../scene-list.service";
import {SceneService} from "../scene.service";
import {CluadeAssistant} from "./assistant/ClaudeAssistant";
import {aiMapping} from "./definitions/aiMapping";
import {FintechService} from "../fintech.service";
import {AiBalanceService} from "../AiBalanceService";



@Injectable({
  providedIn: 'root'
})
export class AiService implements IAiFunctionCalls {
    x = 0;
    y = 0;

    aiSettings;
    aiApis;

    user: IUser;
    resources;

    log$ = new Subject();
    showDialog$ = new BehaviorSubject<boolean>(false);
    busy$ = new BehaviorSubject<boolean>(false);

    private currentRequestSubscription: any = null;

    createImage$ = new Subject();
    imageCreated$;

    sceneCommands$ = new Subject();

    layerCommand$ = new Subject();

    sceneNav$ = new Subject<string>();

    selectPropertyPage$ = new Subject<string>();
    addEffect$ = new Subject();
    removeEffect$ = new Subject();

    backgroundResource$ = new Subject();

    assistant: Assistant;

    // Conversation history management - per scene
    private conversationHistoryMap: Map<string, Array<{
        role: 'user' | 'assistant',
        content: string,
        timestamp: number
    }>> = new Map();
    private currentSceneIdForHistory: string | null = null;
    private MAX_HISTORY_MESSAGES = 10; // Last 5 user-assistant pairs

    sceneList;

    m_stations: Array<IStationData>;


    editorWidth;
    editorHeight;

    players;
    scenePlayerData;
    selectedSceneId;
    selectedItems;
    sceneSlice;
    selectedPlaylistIndexes;

    tutorials;


    imageUrl = '';
    updatedRes;
    resourceId;
    changelistId;
    resourceBytesTotal;



    newSceneData;



    aiBaseUrl = "https://ai.signage.me/";

    constructor(protected http: HttpClient,
                protected dialog: MatDialog,
                protected router: Router,
                protected localStoreService: LocalStoreService,
                protected storeSelector: StoreSelectorsService,
                protected sceneAddService: SceneAddService,
                protected containerService: ContainerService,
                protected tutorialsService: TutorialsService,
                protected appService: AppService,
                protected userService: UserService,
                protected translocoService: TranslocoService,
                protected sceneListService: SceneListService,
                protected sceneService: SceneService,
                protected fintechService: FintechService,
                protected aiBalance: AiBalanceService) {

        this.aiApis = {};
        for(const key in aiMapping) {
            this.aiApis[key] = aiMapping[key].options.reduce((acc, option) => {
                acc[option.name] = { ...option };
                delete acc[option.name].name; // Remove the name field
                return acc;
            }, {});
        }

        this.storeSelector.getUser().subscribe((i_user: IUser) => {
                this.user = i_user;
        });

        this.storeSelector.getResources().subscribe((r) => {
            this.resources = {};
            r.forEach( resource => {
                this.resources[resource.resource_id] = resource;
            });
        });

        this.storeSelector.getLocalStoreUpdates()
            .subscribe((updates) => {
                this.aiSettings = this.localStoreService.getStorageValConfig('ai');
            });


        this.storeSelector.getScenes(null, false)
            .subscribe((i_scenes) => {
                this.sceneList = [];
                if (i_scenes != null) {
                    i_scenes.forEach(item => {
                        const player = item.Player;
                        const scene = {
                            sceneId: player.$.id,
                            sceneName: player.$.label
                        };
                        this.sceneList.push(scene);
                    });
                }
            });

        this.storeSelector.listenPlayerDataOfSelectedSceneItemId()
            .subscribe((i_sceneSlice: ISceneTreeSliceResults) => {
                this.sceneSlice = i_sceneSlice;
                this.editorWidth = +this.sceneSlice.sceneItem.Data[0].Layout[0].$.width;
                this.editorHeight = +this.sceneSlice.sceneItem.Data[0].Layout[0].$.height;
            });

        this.storeSelector.listenSelectedSceneIdAndSceneItemId().subscribe((v: ISelectedSceneSceneId) => {
            const newSceneId = v.sceneId;

            // Detect scene switch - clear history when switching to different scene
            if (this.currentSceneIdForHistory !== null &&
                this.currentSceneIdForHistory !== newSceneId) {
                // Scene switched - clear old scene history and start fresh
                this.conversationHistoryMap.delete(this.currentSceneIdForHistory);
            }

            this.currentSceneIdForHistory = newSceneId;
            this.selectedSceneId = newSceneId;
        });

        this.storeSelector.listenSelectedScene().subscribe((i_scene: IPlayerProps) => {
            if (i_scene && i_scene.Player) {
                this.scenePlayerData = i_scene.Player;
                this.players = (i_scene.Player.Data["0"]?.Scene["0"]?.Players["0"]?.Player || []) as ISceneBlock[];
            } else {
                this.scenePlayerData = null;
                this.players = null;
            }
        });

        this.tutorialsService.getData().subscribe(data => {
            this.tutorials = this.tutorialsService.extractVideoItems(data);
        });

        this.containerService.getActiveSceneItemIds().subscribe((_ids) => {
            this.selectedItems = _ids;
        });

        this.storeSelector.listenSelectedScenePlaylist().subscribe((v) => {
            this.selectedPlaylistIndexes = v;
        });

        this.storeSelector.getStationsComplete()
            .subscribe((v) => {
                this.m_stations = v || [];
            });
    }

    isDialogVisible() {
        return this.showDialog$.value;
    }

    showDialog(visible) {
        this.showDialog$.next(visible);
    }

    addMsg(txt, typ, metadata = {}) {
        const message = {
            text: txt,
            type: typ,
            timestamp: new Date().toISOString(),
            ...metadata
        };
        this.log$.next(message);
    }

    removeLastMsg() {
        const message = {
            type: 'removeLastMsg',
        };
        this.log$.next(message);
    }

    send(assistantName, prompt): Observable<any> {
        this.stopCurrentRequest();
        this.busy$.next(true);

        switch (assistantName) {
            case 'gpt':
                this.assistant = new GptAssistant();
                break;
            case 'gemini':
                this.assistant = new GeminiAssistant();
                break;
            case 'claude':
                this.assistant = new CluadeAssistant();
                break;

        }

        this.addMsg(prompt, 'user', { icon: 'person' });

        // Store user prompt in history
        this.storeUserPrompt(prompt);

        // Inject conversation history FIRST (if exists for current scene)
        this.injectConversationHistory();

        // Then add user message
        this.assistant.addUserMessage(prompt);

        // Add general system messages
        generalInfo.forEach( msg => {
            this.assistant.addSystemMessage(msg);
        });

        // Add critical rule about manual edits
        this.assistant.addSystemMessage(`
CRITICAL RULE: User may have manually edited the scene between conversations.
- ALWAYS call getSceneData() before modifying existing components
- NEVER assume component IDs, positions, or properties from conversation history
- Conversation history shows user intent only, NOT current scene state
- Scene state is always fetched fresh via getSceneData()
        `.trim());


        //this.assistant.addSystemMessage("pageId: " + this.router.url);


        if (this.selectedSceneId != null) {
            this.assistant.addSystemMessage("selectedScene Id: " + this.selectedSceneId);

            const bgId = this.sceneService.getBackgroundImageId();
            if (bgId != null) {
                this.assistant.addSystemMessage("background item Id: " + bgId + ".");
            }

            this.assistant.addSystemMessage("selectedScene Size: " + this.editorWidth + ', ' + this.editorHeight + ".");
            this.assistant.addSystemMessage("when addComponentToScene there is no need to call first getSceneData, call it after the add.");


            this.assistant.addSystemMessage("selectedItems: " + this.selectedItems + ".");
            this.assistant.addSystemMessage("If user ask to update item call 'getSceneData' first.");
            this.assistant.addSystemMessage("for any update select the items first.");

            this.assistant.addSystemMessage("'register player' → call sceneCommand with { 'command': 'REGISTER' }.");
        }





        this.currentRequestSubscription = this.chat().pipe(
            catchError( err => {
                //;
                this.addMsg(err.error || 'An error occurred', 'error', { icon: 'error', severity: 'high' });
                return of(null);
            }),
            last(),
            tap( () => {
                this.busy$.next(false);
            }),
            delay(1000),
            finalize(() => {
                if (this.aiSettings.autoHide) {
                    this.showDialog(false);
                }
            })
        ).subscribe({
            next: () => {},
            error: (err) => {
                this.busy$.next(false);
                this.currentRequestSubscription = null;
            },
            complete: () => {
                this.currentRequestSubscription = null;
            }
        });

        return of(null);
    }

    stopCurrentRequest(): void {
        if (this.currentRequestSubscription) {
            this.currentRequestSubscription.unsubscribe();
            this.currentRequestSubscription = null;
            this.busy$.next(false);
            this.addMsg('<div class="cancelled-message"><span class="material-icons">cancel</span><span>Request cancelled by user</span></div>', 'system', { icon: 'cancel', isHtml: true, severity: 'cancelled' });
        }
    }

    /**
     * Inject conversation history for the current scene into the assistant
     */
    private injectConversationHistory(): void {
        if (!this.currentSceneIdForHistory) {
            return;
        }

        const history = this.conversationHistoryMap.get(this.currentSceneIdForHistory);
        if (!history || history.length === 0) {
            return;
        }

        // Inject last N messages
        const recentHistory = history.slice(-this.MAX_HISTORY_MESSAGES);

        recentHistory.forEach(msg => {
            if (msg.role === 'user') {
                this.assistant.addSystemMessage(`[Previous user request]: ${msg.content}`);
            } else {
                this.assistant.addSystemMessage(`[Previous assistant action]: ${msg.content}`);
            }
        });
    }

    /**
     * Store user prompt in conversation history
     */
    private storeUserPrompt(prompt: string): void {
        if (!this.currentSceneIdForHistory) {
            return;
        }

        if (!this.conversationHistoryMap.has(this.currentSceneIdForHistory)) {
            this.conversationHistoryMap.set(this.currentSceneIdForHistory, []);
        }

        const history = this.conversationHistoryMap.get(this.currentSceneIdForHistory);
        history.push({
            role: 'user',
            content: prompt,
            timestamp: Date.now()
        });
    }

    /**
     * Store assistant response in conversation history (sanitized to remove specific IDs/positions)
     */
    private storeAssistantResponse(response: string): void {
        if (!this.currentSceneIdForHistory) {
            return;
        }

        if (!this.conversationHistoryMap.has(this.currentSceneIdForHistory)) {
            this.conversationHistoryMap.set(this.currentSceneIdForHistory, []);
        }

        // Sanitize response to remove specific details that may become stale
        const sanitized = this.sanitizeResponse(response);

        const history = this.conversationHistoryMap.get(this.currentSceneIdForHistory);
        history.push({
            role: 'assistant',
            content: sanitized,
            timestamp: Date.now()
        });
    }

    /**
     * Sanitize AI response to remove specific IDs, coordinates, and values
     * that may become outdated if user manually edits the scene
     */
    private sanitizeResponse(response: string): string {
        let sanitized = response;

        // Remove HTML tags for cleaner history
        sanitized = sanitized.replace(/<[^>]*>/g, '');

        // Remove specific component IDs (format: word_alphanumeric)
        sanitized = sanitized.replace(/\b\w+_[a-zA-Z0-9]{6,}\b/g, '[component]');

        // Remove coordinate values: "at 100, 200" or "x: 100" etc.
        sanitized = sanitized.replace(/\b(at|x|y|width|height|top|left|right|bottom):\s*\d+/gi, '');
        sanitized = sanitized.replace(/\bat\s+\d+\s*,?\s*\d*/gi, '');

        // Remove specific numeric property values
        sanitized = sanitized.replace(/\b(fontSize|size|opacity|rotation|scale):\s*\d+(\.\d+)?/gi, '');

        // Clean up extra whitespace
        sanitized = sanitized.replace(/\s+/g, ' ').trim();

        return sanitized;
    }

    /**
     * Clear conversation history for current scene
     */
    clearConversationHistory(): void {
        if (this.currentSceneIdForHistory) {
            this.conversationHistoryMap.delete(this.currentSceneIdForHistory);
        }
    }

    /**
     * Clear all conversation history (for all scenes)
     */
    clearAllConversationHistory(): void {
        this.conversationHistoryMap.clear();
    }

    chat(): Observable<any> {
        const prompt = JSON.stringify(this.assistant.getPrompt());

        //;
        const aiUrl = this.aiBaseUrl + this.aiApis.chat[this.aiSettings.chat].api;

        return this.fintechService.getPayerToken().pipe(
            switchMap(payerToken => {
                return this.http.post(aiUrl, {
                    "token": payerToken,
                    "json": prompt,
                    "args": "{}"
                });
            }),
            switchMap(a => {
                const balanceInfo = JSON.parse(<string>((<any>a).balanceInfo));
                //
                this.aiBalance.updateBalance(balanceInfo.balance);

                const reply = JSON.parse(<string>((<any>a).reply));
                const functionList = this.assistant.getFunctionList(reply);
                return from(functionList).pipe(
                    concatMap(func =>
                        of(func).pipe(
                            observeOn(animationFrameScheduler)
                        )
                    )
                );
            }),
            switchMap(b => {
                //
                const part: any = b;
                if (part.text != null) {
                    this.addMsg(part.text, 'assistant', { icon: 'smart_toy', isHtml: true });

                    // Store assistant response in conversation history (sanitized)
                    this.storeAssistantResponse(part.text);

                    return of('a');
                } else if (part.functionCall != null) {

                    if (part.functionCall.name != 'thinking' || this.aiSettings.showThinking) {
                        this.addMsg(`${part.functionCall.name}`, 'function', {
                            icon: 'functions',
                            functionName: part.functionCall.name,
                            args: part.functionCall.args
                        });
                    }
                    const functionId = part.functionCall.id;
                    const functionName = part.functionCall.name;
                    const functionArgs = part.functionCall.args;

                    // Type-safe function call using the interface
                    if (functionName in this && typeof this[functionName] === 'function') {
                        return (this as IAiFunctionCalls)[functionName](functionId, functionName, functionArgs);
                    } else {
                        console.error(`Function ${functionName} not found in IAiFunctionCalls`);
                        return of(null);
                    }
                }
            }),
            last(),
            switchMap(a => {
                if (this.assistant.requiredAnotherChat) {
                    this.assistant.requiredAnotherChat = false;
                    return this.chat();
                } else {
                    return of("finish");
                }
            })
        );
    }


    thinking(functionId, functionName, functionArgs): Observable<any> {
        const result = functionArgs.text;
        this.assistant.addToolCall(functionId, functionName, functionArgs, result);
        return of('a');
    }


    navigateToPage(functionId, functionName, functionArgs): Observable<any> {
        let pageId = functionArgs.pageId;
        if (functionArgs.propertyName != null) {
            switch (functionArgs.propertyName) {
                case "sceneId":
                    const sceneId = '/' + functionArgs.propertyValue;
                    pageId += sceneId;
                    break;

                case "helpName":
                    const link = '/' + functionArgs.propertyValue;
                    pageId += link;
                    break;
            }
        }

        this.router.navigate([pageId]);

        const result = "navigated to page: " + this.router.url;
        this.assistant.addToolCall(functionId, functionName, functionArgs, result);

        return of('navigated');
    }


    getComponentsDefinition(functionId, functionName, functionArgs): Observable<any> {
        const result = componentsDefinition;
        this.assistant.addToolCall(functionId, functionName, functionArgs, result);

        return of('componentsDefinition');
    }

    getSceneList(functionId, functionName, functionArgs): Observable<any> {
        //;
        const result = JSON.stringify(this.sceneList);
        this.assistant.addToolCall(functionId, functionName, functionArgs, result);

        return of('sceneList');
    }

    createScene(functionId, functionName, functionArgs): Observable<any> {
        //;


        const sceneId = _.uniqueId();
        const sceneBlock = this.storeSelector.getBlockLabelByFileType('scene');
        const scene: ISceneBlock = sceneBlock.template;
        const newScene: any = {Player: scene};

        // set scene layout
        newScene.Player.$.id = sceneId;
        newScene.Player.$.player = sceneBlock.id;
        newScene.Player.Data[0].Layout[0].$.width = String(functionArgs.width);
        newScene.Player.Data[0].Layout[0].$.height = String(functionArgs.height);
        newScene.Player.$.label = functionArgs.sceneName;
        newScene.Player.$.mimeType = SceneMimeTypes.Player;


        const saveRecord = preprocessSceneRecord(newScene, null, this.localStoreService.getStorageConfig().studioId, 'add');
        /** create scene and get inserted id */
        return this.userService.crudRecord(saveRecord).pipe(
            switchMap(i_crudReply => {
                //;

                // change scene player id
                const newPlayerId = String(i_crudReply.id);
                newScene.Player.$.id = newPlayerId;
                newScene.Player.$.lastModified = String(new Date().getTime());
                newScene.Player.$.changelist_id = String(i_crudReply.changelist_id);
                this.storeSelector.dispatch(actionAddScene({player: newScene}));

                const saveRecord2 = preprocessSceneRecord(newScene, null, this.localStoreService.getStorageConfig().studioId, 'add');
                this.crudScene('update', newPlayerId, {player_data_value: saveRecord2.body.player_data_value});
                this.router.navigateByUrl(`editor/scene/${newPlayerId}`);


                const result = "scene created:  sceneId: " + newPlayerId + " sceneName: " + functionArgs.sceneName;
                this.assistant.addToolCall(functionId, functionName, functionArgs, result);

                return of('created');
            })
        );
    }


    private crudScene(command: ICRUDCommand, id, body) {
        const saveRecord: ICRUDRecord = {
            studioId: this.localStoreService.getStorageConfig().studioId,
            command,
            primary: 'player_data_id',
            table: 'player_data',
            body,
            id
        };
        this.storeSelector.dispatch(actionCRUDRecord({saveRecord}));
    }


    selectScenes(functionId, functionName, functionArgs): Observable<any> {
        //;

        return of("selected").pipe(
            switchMap( a =>  from(this.router.navigateByUrl("/editor/list"))),
            delay(100),
            switchMap( a => {
                //
                const list = this.sceneList.filter((i_scene) => {
                    return functionArgs.sceneIds.includes(i_scene.sceneId);
                });

                const sceneIds = list.map(scene => scene.sceneId);
                this.sceneListService.selectScenes(sceneIds);

                const result = JSON.stringify(sceneIds);
                this.assistant.addToolCall(functionId, functionName, functionArgs, result);
                return of('a');
            })
        );

    }

    deleteSelectedScenes(functionId, functionName, functionArgs): Observable<any> {
        //;
        this.sceneListService.deleteSelected();
        return of('deleted');
    }


    setAsBackground(functionId, functionName, functionArgs) {
        this.backgroundResource$.next('set');
        return of('done');
    }

    removeBackground(functionId, functionName, functionArgs) {
        const bgId = this.sceneService.getBackgroundImageId();
        if (bgId) {
            this.sceneService.removeSceneItems([bgId], false);
            return of('done');
        }
    }


    getSceneData(functionId, functionName, functionArgs): Observable<any> {
        //;
        const result = JSON.stringify(this.scenePlayerData);
        this.assistant.addToolCall(functionId, functionName, functionArgs, result);
        return of('sceneData');
    }

    getHelpInfo(functionId, functionName, functionArgs): Observable<any> {
        ;
        const result = JSON.stringify(this.tutorials);
        this.assistant.addToolCall(functionId, functionName, functionArgs, result);
        return of('help');
    }


    updateSceneData(functionId, functionName, functionArgs) {
        try {
            const currentSceneData = this.scenePlayerData;

            const sceneDataString = functionArgs.sceneData;

            if (!sceneDataString || typeof sceneDataString !== 'string') {
                throw new Error('sceneData must be a non-empty string');
            }

            this.newSceneData = JSON.parse(sceneDataString);

            if (!this.newSceneData || typeof this.newSceneData !== 'object') {
                throw new Error('Parsed sceneData must be an object');
            }

            if (!this.newSceneData.$ || !this.newSceneData.Data) {
                throw new Error('sceneData must have $ and Data properties (ISceneBlock structure)');
            }

            const updateResult = "Scene data updated successfully";
            this.assistant.addToolCall(functionId, functionName, functionArgs, updateResult);
            return of('updated');
/*
            const dialogRef = this.dialog.open(JsonDiffDialogComponent, {
                data: {
                    leftJson: currentSceneData,
                    rightJson: this.newSceneData
                },
                width: '90vw',
                maxWidth: '1400px'
            });

            return dialogRef.afterClosed().pipe(
                switchMap(result => {
                    if (result === true) {
                        // User clicked OK, execute the scene update
                        this.storeSelector.dispatch(actionUpdateScenePlayer({
                            sceneId: this.selectedSceneId,
                            playerData: this.newSceneData
                        }));

                        this.storeSelector.dispatch(actionCRUDScene({
                            data: {
                                command: 'rebuild',
                                sceneId: this.selectedSceneId,
                                itemIds: []
                            }
                        }));

                        const updateResult = "Scene data updated successfully";
                        this.assistant.addToolCall(functionId, functionName, functionArgs, updateResult);
                        return of('updated');
                    } else {
                        // User cancelled
                        const cancelMsg = "Scene update cancelled by user";
                        this.addMsg(cancelMsg, 'system', { icon: 'cancel' });
                        this.assistant.addToolCall(functionId, functionName, functionArgs, cancelMsg);
                        return of('cancelled');
                    }
                })
            );

 */

        } catch (error) {
            console.error('updateSceneData error:', error);
            const sceneDataString = functionArgs.sceneData;

            // Create detailed error message with the problematic JSON
            let errorMsg = `Failed to update scene data. ${error.message}`;

            if (sceneDataString) {
                // Show a snippet of the JSON around the error location if it's a syntax error
                if (error instanceof SyntaxError && error.message.includes('position')) {
                    const positionMatch = error.message.match(/position (\d+)/);
                    if (positionMatch) {
                        const position = parseInt(positionMatch[1]);
                        const start = Math.max(0, position - 200);
                        const end = Math.min(sceneDataString.length, position + 200);
                        const snippet = sceneDataString.substring(start, end);

                        // Add position marker
                        const markerPos = position - start;
                        const lines = snippet.split('\n');
                        let charCount = 0;
                        let lineWithError = 0;
                        let posInLine = 0;

                        for (let i = 0; i < lines.length; i++) {
                            if (charCount + lines[i].length >= markerPos) {
                                lineWithError = i;
                                posInLine = markerPos - charCount;
                                break;
                            }
                            charCount += lines[i].length + 1; // +1 for newline
                        }

                        errorMsg += `\n\nProblematic JSON section (error at position ${position}):\n`;
                        errorMsg += `${start > 0 ? '...' : ''}${snippet}${end < sceneDataString.length ? '...' : ''}`;
                        errorMsg += `\n${'~'.repeat(posInLine + (start > 0 ? 3 : 0))}^ ERROR HERE`;
                    }
                } else {
                    // For other errors, show first 1000 chars of the JSON
                    const preview = sceneDataString.length > 1000
                        ? sceneDataString.substring(0, 1000) + '...[truncated]'
                        : sceneDataString;
                    errorMsg += `\n\nJSON provided (first 1000 chars):\n${preview}`;
                }
            }

            this.addMsg(errorMsg, 'error', { icon: 'error', severity: 'high' });
            this.assistant.addToolCall(functionId, functionName, functionArgs, errorMsg);
            return of('error');
        }
    }

    selectComponent(functionId, functionName, functionArgs) {
        //;
        const selectedItems = functionArgs.ids;
        this.containerService.activeSceneItemIds.next(selectedItems);


        const result = "assistant should call updateComponentProperty() is needed.";
        this.assistant.addToolCall(functionId, functionName, functionArgs, result);


        return of('selected');
    }


    sceneCommand(functionId, functionName, functionArgs) {
        //
        const command = functionArgs.command;
        this.sceneCommands$.next(command);

        return of('done');
    }



    layerCommand(functionId, functionName, functionArgs) {
        

        this.sceneNav$.next('layers');
        return of('layer').pipe(
            delay(1000),
            tap( () => {
                this.layerCommand$.next(functionArgs.command);
            })
        );
    }

    getResourcesInScene(functionId, functionName, functionArgs) {
        ;
        const resourceIds = functionArgs.resourceIds;

        const resourceInfo = resourceIds.map(id => ({
            name: this.resources[id].resource_name
        }));

        const str = JSON.stringify(resourceInfo);
        const result = `resources: ${str}.`;
        this.assistant.addToolCall(functionId, functionName, functionArgs, result);
        return of('done');
    }

    getStations(functionId, functionName, functionArgs) {
        ;
        const str = JSON.stringify(this.m_stations);
        const result = `stations: ${str}.`;
        this.assistant.addToolCall(functionId, functionName, functionArgs, result);
        return of('done');
    }

    createVideo(functionId, functionName, functionArgs) {
        ;

        this.addMsg('Creating video is not supported yet. will be available soon...', 'error', { icon: 'warning' });
        return of(null);

        this.addMsg('Creating video...', 'process', { icon: 'videocam', loading: true });

        const jsonPayload = {
            prompt: functionArgs.prompt
        };


        const aiUrl = this.aiBaseUrl + this.aiApis.createVideo[this.aiSettings.createVideo].api;
        const args = {
            mode: 'create'
        };

        return this.fintechService.getPayerToken().pipe(
            switchMap(payerToken => {
                return this.http.post(aiUrl, {
                    "token": payerToken,
                    "json": JSON.stringify(jsonPayload),
                    "args": JSON.stringify(args)
                });
            }),
            switchMap(videoInfo => {
                ;
                this.addMsg('Video created', 'success', { icon: 'check_circle' });

                const msg = {
                    functionId, functionName, functionArgs,
                    videoUrl: (<any> videoInfo).videoUrl
                };
                this.createImage$.next(msg);
                //this.showImage((<any> imageInfo).imageUrl, functionArgs.title, functionArgs.size);
                this.imageCreated$ = new Subject();
                return this.imageCreated$;
            }),
            catchError( err => {
                this.addMsg(err.error || 'An error occurred', 'error', { icon: 'error', severity: 'high' });
                return of(null);
            })
        );

    }


    createImage(functionId, functionName, functionArgs) {
        this.addMsg('Creating image using ' + this.aiSettings.createImage, 'process', { icon: 'image', loading: true });

        const jsonPayload = {
            prompt: functionArgs.prompt,
            n: 1,
            size: functionArgs.size  // Optional, depends on your backend
        };

        const args = {
            mode: 'create'
        };

        const aiUrl = this.aiBaseUrl + this.aiApis.createImage[this.aiSettings.createImage].api;

        return this.fintechService.getPayerToken().pipe(
            switchMap(payerToken => {
                return this.http.post(aiUrl, {
                    "token": payerToken,
                    "json": JSON.stringify(jsonPayload),
                    "args": JSON.stringify(args)
                });
            }),
            switchMap(imageInfo => {
                
                this.removeLastMsg();
                this.addMsg('Image created', 'success', { icon: 'check_circle' });

                const msg = {
                    functionId, functionName, functionArgs,
                    imageUrl: (<any> imageInfo).imageUrl,
                    imageBase64: (<any> imageInfo).imageBase64,
                    players: this.players,
                    selectedItems: this.selectedItems,
                    sceneSlice: this.sceneSlice,
                    selectedPlaylistIndexes: this.selectedPlaylistIndexes
                };
                this.createImage$.next(msg);
                this.imageCreated$ = new Subject();
                return this.imageCreated$;
            })
        );

    }

    convertBase64ToUrl(base64String: string): string {
        // If it already has the data URL prefix, return as is
        if (base64String.startsWith('data:image')) {
            return base64String;
        }
        // Otherwise, add the data URL prefix
        return `data:image/png;base64,${base64String}`;
    }

    convertBase64ToFile(base64String: string, fileName: string): File {
        
        // Remove the Base64 header if it exists
        const cleanedBase64 = base64String.replace(/^data:image\/\w+;base64,/, '');

        // Convert Base64 to binary data
        const byteString = atob(cleanedBase64);
        const arrayBuffer = new Uint8Array(byteString.length);

        for (let i = 0; i < byteString.length; i++) {
            arrayBuffer[i] = byteString.charCodeAt(i);
        }

        // Create a Blob and then a File
        const blob = new Blob([arrayBuffer], { type: 'image/png' });
        return new File([blob], fileName, { type: 'image/png' });
    }

    modifyImage(functionId, functionName, functionArgs) {
        this.addMsg('Modifying image...', 'process', { icon: 'edit', loading: true });

        ;
        const resourceId = functionArgs.resourceId;
        const resource  = this.resources[resourceId];
        const url = `https://s3-us-west-2.amazonaws.com/oregon-signage-resources/business${this.user.businessId}/resources/${resourceId}.${resource.resource_type}`;

        const jsonPayload = {
            image_url: url,
            prompt: functionArgs.prompt,
            size: functionArgs.size  // Optional, depends on your backend
        };

        const args = {
            mode: 'edit'
        };

        const aiUrl = this.aiBaseUrl + this.aiApis.editImage[this.aiSettings.editImage].api;

        ;

        return this.fintechService.getPayerToken().pipe(
            switchMap(payerToken => {
                return this.http.post(aiUrl, {
                    "token": payerToken,
                    "json": JSON.stringify(jsonPayload),
                    "args": JSON.stringify(args)
                });
            }),
            switchMap(imageInfo => {
                ;

                if ((<any> imageInfo).imageBase64 != null) {
                    //const url = `data:image/png;base64,${(<any> imageInfo).imageBase64}`;
                    const msg = {
                        functionId, functionName, functionArgs,
                        imageBase64: (<any> imageInfo).imageBase64,
                        players: this.players,
                        selectedItems: this.selectedItems,
                        sceneSlice: this.sceneSlice,
                        selectedPlaylistIndexes: this.selectedPlaylistIndexes
                    };
                    this.createImage$.next(msg);
                    this.imageCreated$ = new Subject();
                    return this.imageCreated$;

                } else {
                    const msg = {
                        functionId, functionName, functionArgs,
                        imageUrl: (<any> imageInfo).imageUrl,
                        players: this.players,
                        selectedItems: this.selectedItems,
                        sceneSlice: this.sceneSlice,
                        selectedPlaylistIndexes: this.selectedPlaylistIndexes
                    };
                    this.createImage$.next(msg);
                    this.imageCreated$ = new Subject();
                    return this.imageCreated$;
                }




            })
        );


        return of('a');
    }

    downloadImageCompleted(imageMessage): Observable<any> {
        ;
        let bodyInfo;
        if (imageMessage.imageBase64 != null) {
            bodyInfo = {
                base64: imageMessage.imageBase64,
                ext: 'png'
            };

        } else {
            bodyInfo = {
                url: imageMessage.imageUrl,
                ext: 'png'
            };
        }


        const saveRecord: any = {
            body: bodyInfo,
            command: "upload",
            primary: "resource_id",
            studioId: this.localStoreService.getStorageConfig().studioId,
            table: "resources",
            ext: 'png'
        };


        this.updatedRes = {
            resource_name: imageMessage.functionArgs.title,
            resource_type: 'png',
            default_player: '3130',
            resource_total_time: 0,
            resource_date_created: new Date().getTime() + '',
            resource_date_modified: new Date().getTime() + '',
            resource_bytes_total: '0',
            access_key: '0',
            tree_path: ''
        };

        return this.userService.crudRecord(saveRecord).pipe(
            switchMap(ret => {
                this.resourceId = (<any> ret).id;
                this.changelistId = (<any> ret).changelist_id;
                this.resourceBytesTotal =  (<any> ret).resource_bytes_total;
                const updateRecord: ICRUDRecord = {
                    studioId: this.localStoreService.getStorageConfig().studioId,
                    command: 'update',
                    primary: 'resource_id',
                    table: 'resources',
                    body: this.updatedRes,
                    id: this.resourceId
                };
                return this.userService.crudRecord(updateRecord);
            }),
            switchMap(ret => {
                //;
                const size = imageMessage.functionArgs.size.split('x');
                this.updatedRes['resource_id'] = String(this.resourceId);
                this.updatedRes['id'] = String(this.resourceId);
                this.updatedRes['changelist_id'] = String((<any> ret).changelist_id);
                this.updatedRes['resource_bytes_total'] = String(this.resourceBytesTotal);
                this.updatedRes['resource_pixel_width'] = size[0];
                this.updatedRes['resource_pixel_height'] = size[1];

                this.storeSelector.dispatch(actionAddResources({data: this.updatedRes as IResource}));


                //;

                return of('downloaded');

            }),
            delay(10),
            switchMap( a => {
                this.addMsg('Image was added to the resources', 'success', { icon: 'check_circle' });

                switch (imageMessage.option) {
                    case 'none':
                        break;
                    case 'addToScene':
                        if (this.selectedSceneId != null) {

                            this.sceneAddService.addBlockType = 'BLOCK_RESOURCES';
                            const block: any = this.sceneAddService.generateBlockData(this.resourceId, "png");


                            const blocks = [
                                block.template
                            ];

                            this.sceneAddService.addScenePlayers(blocks, 0, 0);

                            this.addMsg('Image was added to the scene', 'success', { icon: 'check_circle' });
                        }
                        break;
                    case 'replaceSelectedResource':
                        const values = [
                            {
                                key: 'resource',
                                storePath: 'Data[0].Resource[0].$',
                                value: this.resourceId
                            }
                        ];
                        this.storeSelector.dispatch(actionUpdateSceneItem({
                            sceneId: this.selectedSceneId,
                            sceneItemId: this.selectedItems[0],
                            values: values
                        }));
                        this.addMsg('selected resource in the Scene was replaced with the new created Image', 'success', { icon: 'check_circle' });
                        break;

                    case 'addToSelectedPlaylist':
                        this.addToSelectedPlaylist(imageMessage.sceneSlice.sceneItem);
                        this.addMsg('Image was added to the Playlist', 'success', { icon: 'check_circle' });
                        break;

                    case 'replaceSelectedPlaylistItem':
                        this.replaceSelectedPlaylistItem(imageMessage.sceneSlice.sceneItem, imageMessage.selectedPlaylistIndexes[0]);
                        this.addMsg('selected resource in the Playlist was replaced with the new created Image', 'success', { icon: 'check_circle' });
                        break;
                }


                const result = "Image created";
                this.assistant.addToolCall(imageMessage.functionId, imageMessage.functionName, imageMessage.functionArgs, result);

                this.imageCreated$.next('image-created');
                this.imageCreated$.complete();

                return of('a')
            })
        );

    }

    downloadImageCanceled() {
        this.addMsg('<div class="cancelled-message"><span class="material-icons">cancel</span><span>Request cancelled by user</span></div>', 'system', { icon: 'cancel', isHtml: true, severity: 'cancelled' });
        this.imageCreated$.next('download-canceled');
        this.imageCreated$.complete();

    }

    private addToSelectedPlaylist(sceneItem: ISceneBlock) {
        
        let pager: IPage;
        const pages = sceneItem?.Data[0]?.Playlist[0].Page && _.isArray(sceneItem.Data[0].Playlist[0].Page) ? _.cloneDeep(sceneItem.Data[0].Playlist[0].Page) : [];

        const resource_type = 'png';
        const playerId = (["mp4", "mov", "m4v", "3gp"].indexOf(resource_type) > -1) ? "3100" : "3130";

        pager = {
            $: {
                page: this.updatedRes.resource_name,
                type: "resource"
            },
            Player: [
                {
                    "$": {
                        player: playerId,
                        id: _.uniqueId('__')
                    },
                    Data: [
                        {
                            Resource: [
                                {
                                    $: {
                                        resource: this.resourceId
                                    }
                                }
                            ]
                        }
                    ]
                }
            ]
        };
        pages.push(pager);

        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.selectedSceneId,
            sceneItemId: sceneItem.$.id,
            values: [{
                key: 'Page',
                value: pages,
                storePath: 'Data[0].Playlist[0]'
            }]
        }));
    }

    private replaceSelectedPlaylistItem(sceneItem: ISceneBlock, index) {
        
        let pager: IPage;
        const pages = sceneItem?.Data[0]?.Playlist[0].Page && _.isArray(sceneItem.Data[0].Playlist[0].Page) ? _.cloneDeep(sceneItem.Data[0].Playlist[0].Page) : [];

        const resource_type = 'png';
        const playerId = (["mp4", "mov", "m4v", "3gp"].indexOf(resource_type) > -1) ? "3100" : "3130";

        pager = pages[index];
        pager.$.page = this.updatedRes.resource_name;
        pager.Player[0].$.player = playerId;
        pager.Player[0].Data[0].Resource[0].$.resource = this.resourceId;


        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.selectedSceneId,
            sceneItemId: sceneItem.$.id,
            values: [{
                key: 'Page',
                value: pages,
                storePath: 'Data[0].Playlist[0]'
            }]
        }));
    }

}
